/**
 * SearchBar — explicit-submit search (no live/keystroke querying).
 *
 * Type freely; the query fires ONLY on Enter or the Search button (and an empty submit / Clear
 * resets results). This replaces debounced type-ahead on the event/organization/contact lists,
 * where firing mid-keystroke against large tables caused partial-input timeouts. The Search button
 * is disabled until `minChars` is reached so a too-broad 1-char scan can't be submitted.
 */
import { useState } from "react";

interface SearchBarProps {
  /** Called with the trimmed query on submit; called with "" on clear/empty submit. */
  onSearch: (query: string) => void;
  placeholder?: string;
  /** Seeds the input (e.g. the current applied query). */
  initialValue?: string;
  /** Minimum characters before a non-empty search may be submitted (default 2). */
  minChars?: number;
  className?: string;
}

export default function SearchBar({
  onSearch,
  placeholder = "Search...",
  initialValue = "",
  minChars = 2,
  className,
}: SearchBarProps) {
  const [value, setValue] = useState(initialValue);

  const trimmed = value.trim();
  const canSubmit = trimmed.length === 0 || trimmed.length >= minChars;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) {
      return;
    }
    onSearch(trimmed);
  };

  const handleClear = () => {
    setValue("");
    onSearch("");
  };

  return (
    <form
      onSubmit={handleSubmit}
      role="search"
      className={`flex items-center gap-2 ${className ?? ""}`}
    >
      <div className="relative flex-1">
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={placeholder}
          aria-label={placeholder}
          className="w-full px-4 py-2 pr-9 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        {value && (
          <button
            type="button"
            onClick={handleClear}
            aria-label="Clear search"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        )}
      </div>
      <button
        type="submit"
        disabled={!canSubmit || trimmed.length === 0}
        title={
          trimmed.length > 0 && trimmed.length < minChars
            ? `Enter at least ${minChars} characters`
            : "Search"
        }
        className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Search
      </button>
    </form>
  );
}
